/**
 * BossProjectile.ts
 * Pooled left-firing turret projectile for the World 2 boss (Void Mothership).
 *
 * Sibling to `ShipProjectile` — same pooled-entity shape and pool
 * contract, but the kinematics differ:
 *   - Always travels LEFT (boss faces left toward the ship).
 *   - Despawn cull is the LEFT edge of the visible window, not the right.
 *   - No time-to-live: bolts persist until they cross the cull line
 *     (`ShipProjectile` ages out on a TTL because ship bolts are fired
 *     toward an advancing camera; boss bolts always exit the viewport).
 *
 * Pool contract: implements `reset()` for `ObjectPool` recycling. Always
 * acquire from `bossProjectilePool` (see `bossSystem.ts`) rather than
 * `new`-ing directly outside of pool pre-warm.
 *
 * Ship-damage collision is handled in `shipDamage.ts` against the
 * `boss-projectile` body kind — see `shipDamage.bodies.test.ts` for
 * coverage. This file owns kinematics + pool lifecycle only.
 *
 * NO React imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift (boss Phase 1 — turret-only).
 *
 * @module BossProjectile
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import {
  VOID_MOTHERSHIP_PROJECTILE_WIDTH,
  VOID_MOTHERSHIP_PROJECTILE_HEIGHT,
  VOID_MOTHERSHIP_PROJECTILE_SPEED,
} from '../../constants/bossConstants';

/**
 * Off-screen margin (px) added to the LEFT edge of the visible window
 * before a boss projectile is considered despawned. Mirrors
 * `SHIP_PROJECTILE_OFFSCREEN_MARGIN` so a turret bolt that just barely
 * clears the viewport edge isn't recycled mid-frame while still
 * visible.
 */
export const BOSS_PROJECTILE_OFFSCREEN_MARGIN = 32;

/**
 * BossProjectile — pooled left-travelling projectile for boss turret fire.
 *
 * Acquired by `bossSystem` on each turret tick during the boss's HOLD
 * state. Damages the ship on contact via `shipDamage.ts`.
 */
export class BossProjectile extends BaseEntity {
  /** Monotonic ID counter for stable React keys across pool recycling. */
  private static nextId = 1;

  /** Unique spawn identifier — re-issued each `spawn()` call. */
  public spawnId = 0;

  /** True while this projectile is actively travelling. */
  public active = false;

  constructor() {
    super(0, 0, VOID_MOTHERSHIP_PROJECTILE_WIDTH, VOID_MOTHERSHIP_PROJECTILE_HEIGHT);
    this.dead = true;
  }

  /**
   * Initialise the projectile at a world position. Always travels left.
   *
   * @param x - Spawn X in world space (logical px).
   * @param y - Spawn Y in world space (logical px).
   */
  public spawn(x: number, y: number): void {
    this.spawnId = BossProjectile.nextId++;
    this.x = x;
    this.y = y;
    this.vx = -VOID_MOTHERSHIP_PROJECTILE_SPEED;
    this.vy = 0;
    this.dead = false;
    this.active = true;
  }

  /** Reset state for pool recycling. Called by `ObjectPool.release()`. */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.dead = true;
    this.active = false;
  }

  /**
   * Advance the projectile by one fixed timestep. Despawns when its
   * RIGHT edge drops below the LEFT cull line of the viewport.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused today; kept for `BaseEntity.update` contract.
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    // DECISION: Use camera-relative left-edge cull so the projectile
    // always despawns once it has visibly cleared the viewport — symmetric
    // with `ShipProjectile`'s right-edge cull. Compare the projectile's
    // RIGHT edge to the cull line so the entire sprite has left view.
    const leftCullX = spaceState.cameraX - BOSS_PROJECTILE_OFFSCREEN_MARGIN;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
    }
  }
}
